import {Injectable}                                from '@angular/core';
import {combineLatest, Subscription}               from 'rxjs';
import {filter, map}                               from 'rxjs/operators';
import {Create}                                    from 'ts-tooling';
import {
  IModalDialogResult
}                                                  from '../../../ap-interface/interfaces/ap-modaldialog-data.interface';
import {ModalDialogButtonKeys, ModalDialogPresets} from '../../../stores/dialog/modal.dialog.presets';
import {SoilSampleDateStore}                       from '../../../stores/nutrients/soilsampledate.store';
import {SoilSampleDateService}                     from '../soil-sample-date.service';
import {CampaignYearStore}                         from '../../../stores/login/campaignyear.store';
import {FieldStore}                                from '../../../stores/farm/field.store';
import {TranslationStore}                          from '../../../stores/translation/translation.store';
import {GetRoundNumericService}                    from '../../../ap-utils/service/get-round-numeric.service';
import {LicenseStore}                              from '../../../stores/farm/license.store';
import {ModalDialogStore}                          from '../../../stores/dialog/modal.dialog.store';
import {ApDateService, DateDiffType}               from '../../../ap-core/services/ap-date-service';
import {LicenseService}                            from './license.service';
import {LoginStore}                                from '../../../stores/login/login.store';
import {ApFeatureModulKey}                         from '../../../ap-interface/enums/ap-feature-modul-key.enum';
import {LicenseAreaCheckService}                   from './license-area-check.service';
import ILicense = Data.Licensing.ILicense;
import IField = Data.FieldManagement.IField;
import ICampaignYear = Data.Authentication.ICampaignYear;
import LicenseAdjustAction = Data.Licensing.Enums.LicenseAdjustAction;

@Injectable({providedIn: 'root'})
export class LicenseNotificationService {
  private _baseFertilizationLicenseSubscription: Subscription;

  constructor(private fieldStore: FieldStore,
              private loginStore: LoginStore,
              private licenseStore: LicenseStore,
              private modalDialogStore: ModalDialogStore,
              private translationStore: TranslationStore,
              private campaignYearStore: CampaignYearStore,
              private soilSampleDateStore: SoilSampleDateStore,
              private dateService: ApDateService,
              private licenseService: LicenseService,
              private roundService: GetRoundNumericService,
              private soilSampleDatesService: SoilSampleDateService,
              private licenseAreaCheckService: LicenseAreaCheckService,
              private modalDialogPresets: ModalDialogPresets) {
  }

  /**
   * Checks the base fertilization license and triggers adjustment logic
   * This method:
   * - Monitors license and field data
   * - Compares licensed area with actual field area
   * - Displays a modal dialog if the area exceeds license threshold
   * - Allows user to extend or skip license adjustment
   * Subscription is managed to prevent multiple concurrent checks
   * @public
   */
  public invokeBaseFertilisationLicenseAdjustmentLogic(): void {
    // Here we show a modal window about license information with an ability to extend it.
    // It shows in case when the amount of area for the current campaign year is > or < 10 % from the licensed base fertilisation modul feature area
    if (!!this._baseFertilizationLicenseSubscription) {
      return;
    }
    this._baseFertilizationLicenseSubscription = combineLatest([
      this.loginStore.baseDataLoaded$,
      this.licenseStore.licenseData$,
      this.fieldStore.Fields$,
      this.campaignYearStore.SelectedCampaignYear$,
      this.soilSampleDateStore.Dates$,
      this.loginStore.SelectedFarm$
    ]).pipe(
      // Filter data presence, data loading and is campaign year latest
      filter(([loadedData, licenses, fields, selectedCampaignYear, _, selectedFarm]) => {
        return loadedData.every(loaded => loaded) && !!selectedFarm?.Id && !!selectedCampaignYear?.Year
          && licenses?.length > 0 && fields?.length > 0 && this.licenseService.isLatestLicensedYear();
      }),
      // Map needed data for filtering and data usage for calculations
      map(([, , fields, selectedCampaignYear, soilSampleDates, selectedFarm]) => {
        const fieldsWithValidSoilSample = this.soilSampleDatesService.getFieldsWithValidSoilSampleDate(fields, soilSampleDates)
          .filter(x => x.FarmId === selectedFarm.Id);
        const license = this.licenseService.getValidBaseFertilizationLicense();
        return ([license, fieldsWithValidSoilSample, selectedCampaignYear] as [ILicense, IField[], ICampaignYear]);
      }),
      // Filter data to be sure that it meets conditions to check license
      filter(([license, fields, _]) => {
        return !!license && !this._checkIsTrialPeriodValid(license?.TrialExpireAt)
          && fields.length > 0
          && license.QuantityErp && !license.NoCheck;
      })
    ).subscribe(([license, fields, selectedCampaignYear]) => {
      const fieldsAreaHaSum = fields.map(x => this.fieldStore.getFieldGeom(x, selectedCampaignYear))
        .reduce((total, current) => total + Create(current?.AreaHa, 0), 0);
      const isAreaTooSmall = this.licenseAreaCheckService.isBaseFertilizationAreaTooSmall(license?.QuantityErp, fieldsAreaHaSum);
      if (!isAreaTooSmall) {
        this._baseFertilizationLicenseSubscription.unsubscribe();
        this._baseFertilizationLicenseSubscription = undefined;
        return;
      }
      const farmQuantityTranslation = this.translationStore.FindTranslationForSelectedLanguage('InfoText__Farm_Area_Quantity');
      const licenseQuantityTranslation = this.translationStore.FindTranslationForSelectedLanguage('InfoText__Licensed_Area_Quantity');
      const modalWindowMessage = `\r\n\r\n${farmQuantityTranslation}: ${this.roundService.round(fieldsAreaHaSum)} ha` +
        `\r\n${licenseQuantityTranslation}: ${license.QuantityErp} ha`;
      this.modalDialogStore.setModalDialogData(this.modalDialogPresets.ExtendLicenseDialog({
        message: modalWindowMessage,
        resultDelegate: (dialogResult: IModalDialogResult): void => {
          let adjustAction = LicenseAdjustAction.Skip;
          if (dialogResult?.key === ModalDialogButtonKeys.Confirm) {
            adjustAction = LicenseAdjustAction.Adjust;
          }
          this.licenseStore.adjustLicenseRequest(ApFeatureModulKey.BaseFertilization, adjustAction);
          this._baseFertilizationLicenseSubscription.unsubscribe();
          this._baseFertilizationLicenseSubscription = undefined;
        }
      }));
    });
  }

  /**
   * Determines if the trial period for a license is still valid
   * Checks if the trial expiration date is for today or in the future
   * @param trialExpireAt Optional trial expiration date
   * @returns Boolean indicating if a trial period is still valid
   * @private
   */
  private _checkIsTrialPeriodValid(trialExpireAt?: Date): boolean {
    if (trialExpireAt) {
      const currentDate = this.dateService.getDateMidnight(this.dateService.getFarmDate());
      const dayDifference = this.dateService.getDateDiff(new Date(trialExpireAt), currentDate,
        DateDiffType.Days);
      if (dayDifference >= 0) {
        return true;
      }
    }
    return false;
  }
}
